import { Box, Typography, alpha } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';

import {
  ESecondaryInvestmentSortOption,
  secondaryInvestmentSortVariantsMap,
} from 'components/filters/secondary-investment';
import { SortIcon } from 'components';
import { SortOrder } from 'types';

import { useTableRowStyles } from '../styles';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
  },

  cell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },

  firstCell: {},

  lastCell: {
    minHeight: 'auto',
    paddingRight: '8px',
    justifyContent: 'flex-end',
    color: alpha(theme.palette.text.secondary, 0.36),
  },

  sortIcon: {
    fontSize: '20px',
  },

  tooltip: {
    color: alpha(theme.palette.text.secondary, 0.36),
  },
}));

interface Props {
  sort: { sortBy: string; sortOrder: SortOrder } | undefined;
  onSortChange: (sortState: { sortBy: string; sortOrder: SortOrder } | undefined) => void;
}

export const SecondaryInvestmentHeader = ({ sort, onSortChange }: Props) => {
  const tableRowClasses = useTableRowStyles();
  const classes = useStyles();

  const getSortState = (sortOption: ESecondaryInvestmentSortOption) => {
    if (!sort) return undefined;
    const sortValue = secondaryInvestmentSortVariantsMap[sortOption][sort.sortOrder];
    if (sortValue.sortBy === sort.sortBy) return sort.sortOrder;
    return undefined;
  };

  const handleSortClick = (sortOption: ESecondaryInvestmentSortOption) => {
    return () => {
      if (sort) {
        const sortValue = secondaryInvestmentSortVariantsMap[sortOption][sort.sortOrder];

        if (sortValue.sortBy === sort.sortBy) {
          switch (sort.sortOrder) {
            case SortOrder.ASC:
              return onSortChange(secondaryInvestmentSortVariantsMap[sortOption][SortOrder.DESC]);

            case SortOrder.DESC:
              return onSortChange(undefined);
          }
        }
      }

      onSortChange(secondaryInvestmentSortVariantsMap[sortOption][SortOrder.ASC]);
    };
  };

  return (
    <Box className={classes.card}>
      <Box display="flex" flexGrow={1}>
        <Box
          className={clsx(
            tableRowClasses.cell,
            classes.cell,
            tableRowClasses.idCell,
            classes.firstCell,
          )}
        >
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="investment_id" />
          </Typography>
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.riskCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="rating" />
          </Typography>
          <SortIcon
            className={classes.sortIcon}
            sortOrder={getSortState(ESecondaryInvestmentSortOption.Rating)}
            onClick={handleSortClick(ESecondaryInvestmentSortOption.Rating)}
          />
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.productTypeCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="type" />
          </Typography>
          <SortIcon
            className={classes.sortIcon}
            sortOrder={getSortState(ESecondaryInvestmentSortOption.ProductType)}
            onClick={handleSortClick(ESecondaryInvestmentSortOption.ProductType)}
          />
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.timeLeftCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="terms_left" />
          </Typography>
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.irrCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="interest" />
          </Typography>
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.priceCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="nominal_amount" />
          </Typography>
          <SortIcon
            className={classes.sortIcon}
            sortOrder={getSortState(ESecondaryInvestmentSortOption.InvestmentAmount)}
            onClick={handleSortClick(ESecondaryInvestmentSortOption.InvestmentAmount)}
          />
        </Box>

        <Box className={clsx(tableRowClasses.cell, tableRowClasses.priceCell, classes.cell)}>
          <Typography variant="caption" color="text.secondary" noWrap>
            <FormattedMessage id="selling_price" />
          </Typography>
          <SortIcon
            className={classes.sortIcon}
            sortOrder={getSortState(ESecondaryInvestmentSortOption.InvestmentAmount)}
            onClick={handleSortClick(ESecondaryInvestmentSortOption.InvestmentAmount)}
          />
        </Box>
      </Box>

      <Box
        className={clsx(
          tableRowClasses.cell,
          tableRowClasses.inputCell,
          classes.cell,
          classes.lastCell,
        )}
      >
      </Box>
    </Box>
  );
};
